// src/components/FeedbackWidget.jsx
// Floating feedback button — appears on all authenticated user pages
// Submits to POST /api/feedback (saved to DB, visible to admin)
// Pass guestMode={true} on the landing page — skips session auth, asks for email instead

import { useState, useEffect, useRef } from 'react';
import { api } from '../utils/api.js';

const CATEGORIES = [
  { id: 'feature', label: '💡 Feature idea' },
  { id: 'bug',     label: '🐛 Bug report'   },
  { id: 'ux',      label: '🎨 UI / UX'      },
  { id: 'other',   label: '💬 Other'         },
];

const BOT_INTROS = [
  { id: 'greet', from: 'bot', text: '👋 Hello! Welcome to KFX Journaling.' },
  { id: 'help',  from: 'bot', text: "I'm here to collect your ideas & feedback. What's on your mind today?" },
];

function isValidEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

// CSS injected once
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600&display=swap');

  @keyframes kfx-bubble-in {
    0%   { opacity: 0; transform: translateY(8px) scale(.96); }
    100% { opacity: 1; transform: translateY(0)   scale(1);   }
  }
  @keyframes kfx-panel-in {
    0%   { opacity: 0; transform: translateY(16px) scale(.97); }
    100% { opacity: 1; transform: translateY(0)    scale(1);   }
  }
  @keyframes kfx-dot {
    0%, 80%, 100% { transform: translateY(0); opacity: .4; }
    40%           { transform: translateY(-5px); opacity: 1; }
  }
  @keyframes kfx-pulse-ring {
    0%   { transform: scale(1);    opacity: .6; }
    100% { transform: scale(1.65); opacity: 0;  }
  }
  @keyframes kfx-sent-pop {
    0%   { transform: scale(.7);  opacity: 0; }
    60%  { transform: scale(1.1); opacity: 1; }
    100% { transform: scale(1);   opacity: 1; }
  }

  .kfx-msg {
    animation: kfx-bubble-in .3s cubic-bezier(.22,1,.36,1) both;
  }
  .kfx-panel-wrap {
    animation: kfx-panel-in .28s cubic-bezier(.22,1,.36,1) both;
  }
  .kfx-dot-1 { animation: kfx-dot 1.2s infinite ease-in-out .0s; }
  .kfx-dot-2 { animation: kfx-dot 1.2s infinite ease-in-out .2s; }
  .kfx-dot-3 { animation: kfx-dot 1.2s infinite ease-in-out .4s; }

  .kfx-pill:hover {
    background: rgba(201,168,76,.22) !important;
    border-color: rgba(201,168,76,.6) !important;
    color: #C9A84C !important;
  }
  .kfx-submit:not(:disabled):hover {
    background: linear-gradient(135deg,#e0bc62,#b88c30) !important;
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(201,168,76,.35);
  }
  .kfx-close:hover { color: rgba(240,234,214,.8) !important; }
  .kfx-fab:hover .kfx-fab-ring { opacity: 1 !important; }

  .kfx-input:focus {
    border-color: rgba(201,168,76,.5) !important;
    background: rgba(255,255,255,.07) !important;
    outline: none;
  }
  .kfx-sent-pop { animation: kfx-sent-pop .45s cubic-bezier(.22,1,.36,1) both; }
`;

export default function FeedbackWidget({ guestMode = false }) {
  const [open,     setOpen]     = useState(false);
  const [category, setCategory] = useState('feature');
  const [email,    setEmail]    = useState('');
  const [text,     setText]     = useState('');
  const [sent,     setSent]     = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [msgs,     setMsgs]     = useState([]);
  const [typing,   setTyping]   = useState(false);
  const [panelKey, setPanelKey] = useState(0);
  const bottomRef = useRef(null);

  // Inject styles once
  useEffect(() => {
    if (document.getElementById('kfx-fb-styles')) return;
    const el = document.createElement('style');
    el.id = 'kfx-fb-styles';
    el.textContent = STYLES;
    document.head.appendChild(el);
  }, []);

  // Animate bot intro messages when panel opens
  useEffect(() => {
    if (!open) return;
    setMsgs([]);
    setTyping(true);
    const t1 = setTimeout(() => {
      setTyping(false);
      setMsgs([BOT_INTROS[0]]);
      setTyping(true);
    }, 700);
    const t2 = setTimeout(() => {
      setTyping(false);
      setMsgs(BOT_INTROS);
    }, 1800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [open, panelKey]);

  // Auto-scroll messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgs, typing]);

  const canSubmit = text.trim() && !loading && (!guestMode || isValidEmail(email));

  async function handleSubmit() {
    if (!canSubmit) return;

    // Echo user message
    const userMsg = { id: 'u-' + Date.now(), from: 'user', text: text.trim(), tag: CATEGORIES.find(c => c.id === category)?.label };
    setMsgs(m => [...m, userMsg]);
    const captured = text.trim();
    setText('');
    setError('');
    setLoading(true);

    // Typing indicator while sending
    setTyping(true);
    try {
      const payload = {
        category,
        message: captured,
        ...(guestMode && { email: email.trim() }),
      };

      if (guestMode) {
        const res = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000/api'}/feedback/guest`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data?.message || data?.error || 'Failed to send. Please try again.');
        }
      } else {
        await api(`${import.meta.env.VITE_API_URL || 'http://localhost:5000/api'}/feedback`, { method: 'POST', body: JSON.stringify(payload) });
      }

      setTyping(false);
      setMsgs(m => [...m, { id: 'b-ok', from: 'bot', text: '🙏 Thank you very much! Your feedback has been received. We\'ll review it and get back to you soon.' }]);
      setSent(true);
      setEmail('');
      setCategory('feature');
      setTimeout(() => setSent(false), 6000);
    } catch (err) {
      setTyping(false);
      setError(err?.message || 'Failed to send. Please try again.');
      setMsgs(m => [...m, { id: 'b-err', from: 'bot', text: '⚠️ ' + (err?.message || 'Failed to send. Please try again.') }]);
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit();
  }

  function handleClose() {
    setOpen(false);
    setSent(false);
    setText('');
    setEmail('');
    setError('');
  }

  function handleOpen() {
    setOpen(o => {
      if (!o) setPanelKey(k => k + 1); // re-trigger intro animation
      return !o;
    });
  }

  const inputBase = {
    width: '100%', boxSizing: 'border-box',
    background: 'rgba(255,255,255,.05)',
    border: '1px solid rgba(255,255,255,.1)',
    borderRadius: 8,
    color: '#F0EAD6',
    fontSize: 13,
    lineHeight: 1.6,
    padding: '9px 12px',
    fontFamily: "'DM Sans', sans-serif",
    transition: 'border-color .18s, background .18s',
  };

  return (
    <>
      {/* ── FAB ── */}
      <button
        onClick={handleOpen}
        title="Send feedback"
        className="kfx-fab"
        style={{
          position: 'fixed', bottom: 28, right: 28, zIndex: 8000,
          width: 52, height: 52, borderRadius: '50%',
          background: open
            ? 'rgba(14,14,14,.95)'
            : 'linear-gradient(135deg,#C9A84C,#9a7220)',
          border: open ? '1px solid rgba(201,168,76,.35)' : 'none',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: open
            ? '0 4px 20px rgba(0,0,0,.5)'
            : '0 4px 22px rgba(201,168,76,.45)',
          transition: 'all .25s cubic-bezier(.22,1,.36,1)',
        }}
      >
        {/* Pulse ring when closed */}
        {!open && (
          <span style={{
            position: 'absolute', inset: 0, borderRadius: '50%',
            border: '2px solid rgba(201,168,76,.55)',
            animation: 'kfx-pulse-ring 2s cubic-bezier(.215,.61,.355,1) infinite',
          }} className="kfx-fab-ring" />
        )}
        {open
          ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#050505" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
        }
      </button>

      {/* ── Chat panel ── */}
      {open && (
        <div
          key={panelKey}
          className="kfx-panel-wrap"
          style={{
            position: 'fixed', bottom: 92, right: 28, zIndex: 7999,
            width: 340,
            background: '#0c0c0c',
            border: '1px solid rgba(201,168,76,.18)',
            borderRadius: 16,
            boxShadow: '0 24px 64px rgba(0,0,0,.8), 0 0 0 1px rgba(255,255,255,.03)',
            overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
            maxHeight: '78vh',
          }}
        >
          {/* Header */}
          <div style={{
            padding: '14px 16px 13px',
            background: 'linear-gradient(180deg, rgba(201,168,76,.12) 0%, transparent 100%)',
            borderBottom: '1px solid rgba(255,255,255,.06)',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{
              width: 34, height: 34, borderRadius: '50%',
              background: 'linear-gradient(135deg,#C9A84C,#7a5a18)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, flexShrink: 0,
              boxShadow: '0 2px 10px rgba(201,168,76,.3)',
            }}>🦁</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 15, letterSpacing: '.08em', color: '#C9A84C' }}>KFX Assistant</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 1 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80', display: 'inline-block', boxShadow: '0 0 6px #4ade80' }} />
                <span style={{ fontSize: 10, color: 'rgba(240,234,214,.35)', fontFamily: "'DM Sans', sans-serif" }}>Online · Send feedback</span>
              </div>
            </div>
            <button className="kfx-close" onClick={handleClose} style={{
              background: 'none', border: 'none',
              color: 'rgba(240,234,214,.3)', cursor: 'pointer',
              fontSize: 22, lineHeight: 1, padding: '0 2px',
              transition: 'color .15s',
            }}>×</button>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: '14px 14px 8px',
            display: 'flex', flexDirection: 'column', gap: 10,
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(201,168,76,.15) transparent',
          }}>
            {msgs.map((m, i) => (
              <div
                key={m.id}
                className="kfx-msg"
                style={{
                  animationDelay: `${i * 0.04}s`,
                  display: 'flex',
                  justifyContent: m.from === 'user' ? 'flex-end' : 'flex-start',
                  flexDirection: 'column',
                  alignItems: m.from === 'user' ? 'flex-end' : 'flex-start',
                  gap: 4,
                }}
              >
                {m.tag && (
                  <span style={{
                    fontSize: 9, letterSpacing: '.07em', textTransform: 'uppercase',
                    color: 'rgba(201,168,76,.5)', fontFamily: "'DM Sans', sans-serif",
                    marginRight: 2,
                  }}>{m.tag}</span>
                )}
                <div style={{
                  maxWidth: '82%',
                  padding: '9px 13px',
                  borderRadius: m.from === 'user'
                    ? '14px 14px 3px 14px'
                    : '14px 14px 14px 3px',
                  background: m.from === 'user'
                    ? 'linear-gradient(135deg,#C9A84C,#9a7220)'
                    : 'rgba(255,255,255,.06)',
                  border: m.from === 'user'
                    ? 'none'
                    : '1px solid rgba(255,255,255,.08)',
                  color: m.from === 'user' ? '#0c0c0c' : '#F0EAD6',
                  fontSize: 13,
                  fontFamily: "'DM Sans', sans-serif",
                  lineHeight: 1.55,
                  fontWeight: m.from === 'user' ? 500 : 400,
                }}>
                  {m.text}
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {typing && (
              <div className="kfx-msg" style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
                <div style={{
                  padding: '10px 14px',
                  borderRadius: '14px 14px 14px 3px',
                  background: 'rgba(255,255,255,.06)',
                  border: '1px solid rgba(255,255,255,.08)',
                  display: 'flex', gap: 4, alignItems: 'center',
                }}>
                  {['kfx-dot-1','kfx-dot-2','kfx-dot-3'].map(cls => (
                    <span key={cls} className={cls} style={{
                      width: 6, height: 6, borderRadius: '50%',
                      background: 'rgba(201,168,76,.6)',
                      display: 'inline-block',
                    }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input area */}
          {!sent ? (
            <div style={{
              padding: '10px 14px 14px',
              borderTop: '1px solid rgba(255,255,255,.06)',
              background: 'rgba(0,0,0,.3)',
              display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              {/* Email — guest only */}
              {guestMode && (
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="kfx-input"
                  style={{ ...inputBase, fontSize: 12 }}
                />
              )}
              {guestMode && email && !isValidEmail(email) && (
                <div style={{ fontSize: 10, color: '#D94F4F', marginTop: -6, fontFamily: "'DM Sans', sans-serif" }}>Please enter a valid email.</div>
              )}

              {/* Category pills */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {CATEGORIES.map(c => (
                  <button
                    key={c.id}
                    onClick={() => setCategory(c.id)}
                    className="kfx-pill"
                    style={{
                      padding: '4px 9px', fontSize: 11, cursor: 'pointer',
                      background: category === c.id ? 'rgba(201,168,76,.2)' : 'rgba(255,255,255,.04)',
                      border: `1px solid ${category === c.id ? 'rgba(201,168,76,.55)' : 'rgba(255,255,255,.08)'}`,
                      color: category === c.id ? '#C9A84C' : 'rgba(240,234,214,.45)',
                      borderRadius: 20,
                      fontFamily: "'DM Sans', sans-serif",
                      transition: 'all .15s',
                    }}
                  >{c.label}</button>
                ))}
              </div>

              {/* Textarea + send row */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <textarea
                  value={text}
                  onChange={e => { setText(e.target.value); setError(''); }}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your idea… (⌘↵ to send)"
                  rows={2}
                  className="kfx-input"
                  style={{ ...inputBase, resize: 'none', flex: 1, borderRadius: 10 }}
                />
                <button
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  className="kfx-submit"
                  style={{
                    width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                    background: canSubmit
                      ? 'linear-gradient(135deg,#C9A84C,#9a7220)'
                      : 'rgba(201,168,76,.1)',
                    border: '1px solid rgba(201,168,76,.25)',
                    cursor: canSubmit ? 'pointer' : 'not-allowed',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all .2s',
                  }}
                >
                  {loading
                    ? <span style={{ width: 14, height: 14, border: '2px solid rgba(5,5,5,.3)', borderTopColor: '#050505', borderRadius: '50%', display: 'inline-block', animation: 'kfx-dot .8s linear infinite' }} />
                    : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={canSubmit ? '#050505' : 'rgba(201,168,76,.3)'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                  }
                </button>
              </div>

              {error && (
                <div style={{ fontSize: 11, color: '#D94F4F', fontFamily: "'DM Sans', sans-serif" }}>{error}</div>
              )}
            </div>
          ) : (
            <div style={{ padding: '10px 14px 14px', borderTop: '1px solid rgba(255,255,255,.06)', background: 'rgba(0,0,0,.3)' }}>
              <div className="kfx-sent-pop" style={{
                padding: '10px 14px', borderRadius: 10,
                background: 'rgba(74,222,128,.07)',
                border: '1px solid rgba(74,222,128,.2)',
                fontSize: 12, color: '#4ade80',
                fontFamily: "'DM Sans', sans-serif",
                textAlign: 'center',
              }}>
                ✅ Feedback received — Thank you for helping us improve KFX Journaling! We appreciate your input and will review it carefully. If we have any questions or updates, we'll reach out to you at {guestMode ? email : 'your registered email'}. Thanks again!
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}